import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import TimePicker from '@mui/lab/TimePicker';
import {
  getImageUrl,
  ImageLoader,
  isImage,
  LoadingButton,
  NotificationTypeEnum,
  useDialog,
  useNotification,
} from '@prismamedia/one-components';
import { convertFromRaw } from 'draft-js';
import { FC, useState } from 'react';
import {
  BrandKey,
  ContentType,
  FlashSenderProgramStatus,
} from '../../../__generated__/queries-newsletter';
import { useProgramNLFlash } from './programNLFlash.newsletter.graphql';
import { auth } from '../../../utils/auth';
import { getArticlePublicUrl } from '../../../utils/url';
import { ArticleState } from '../reducer';

type SendMode = 'immediate' | 'scheduled';

export const NLFlashDialog: FC<{
  article: ArticleState;
  resource?: ContentType;
  imageUrl?: string;
  onClose?: () => void;
}> = ({
  article,
  resource = ContentType.ARTICLE,
  imageUrl: imageUrlProp,
  onClose,
}) => {
  const { closeDialog } = useDialog();
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [articleLead, setArticleLead] = useState(
    article.lead ? convertFromRaw(article.lead).getPlainText() : '',
  );
  const [sendMode, setSendMode] = useState<SendMode>('immediate');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(9, 0, 0, 0);
    return d;
  });
  const { pushNotification } = useNotification();
  const programNLFlash = useProgramNLFlash();

  const firstImage = article.medias?.find((media) => isImage(media));
  const imageUrl =
    imageUrlProp ?? (firstImage ? getImageUrl(firstImage) : undefined);
  const contentUrl = getArticlePublicUrl(article);
  const isDiapo = resource === ContentType.DIAPO;
  const formatScheduledAt = (date: Date) =>
    date
      .toISOString()
      .replace('T', ' ')
      .replace(/\.\d{3}Z$/, '');

  const onValidate = async () => {
    if (!imageUrl) return;

    if (!subject.trim()) {
      pushNotification({
        message: "L'objet de la NL Flash est requis",
        type: NotificationTypeEnum.error,
      });
      return;
    }

    if (!contentUrl) {
      pushNotification({
        message: 'URL missing for this article',
        type: NotificationTypeEnum.error,
      });
      return;
    }

    if (sendMode === 'scheduled' && (!scheduledDate || !scheduledTime)) {
      pushNotification({
        message: 'Date et heure de programmation requises',
        type: NotificationTypeEnum.error,
      });
      return;
    }

    if (!auth.user?.email) {
      throw new Error("Impossible de récupérer l'email de l'utilisateur");
    }

    const scheduledAt = (() => {
      if (sendMode === 'immediate') {
        const date = new Date();
        date.setMinutes(date.getMinutes() + 1);
        return date;
      }

      const date = new Date(scheduledDate as Date);
      const time = scheduledTime as Date;
      date.setHours(time.getHours(), time.getMinutes(), 0, 0);
      return date;
    })();

    setLoading(true);
    try {
      await programNLFlash({
        variables: {
          data: {
            brandKey: (article.brandKey as string) as BrandKey,
            subject,
            teaser: articleLead,
            img: imageUrl,
            imgAlt: article.title,
            title: article.title,
            contentUrl,
            uuid: article.id,
            status: FlashSenderProgramStatus.SCHEDULED,
            resource,
            scheduledAt: formatScheduledAt(scheduledAt),
            user: auth.user.email,
          },
        },
      });
      pushNotification({
        message: 'Newsletter flash envoyé',
        type: NotificationTypeEnum.success,
      });
      onClose?.();
      closeDialog();
    } catch (error) {
      pushNotification({
        message: (error as Error).message,
        type: NotificationTypeEnum.error,
      });
    }
    setLoading(false);
  };

  return (
    <>
      <DialogTitle>Newsletter Flash</DialogTitle>
      <DialogContent sx={{ width: 600 }}>
        <TextField
          multiline
          required
          fullWidth
          label="Objet"
          value={subject}
          onChange={({ target }) => setSubject(target.value)}
          sx={{ mt: 1 }}
        />
        <TextField
          multiline
          required
          fullWidth
          label="Pré-header"
          value={articleLead}
          onChange={({ target }) => setArticleLead(target.value)}
          sx={{ my: 2 }}
        />
        <ImageLoader
          src={imageUrl}
          loaderSize={450 / 4}
          sx={{ minHeight: 200, borderRadius: 1, overflow: 'hidden' }}
        />
        {!imageUrl && (
          <Typography color="error" sx={{ textAlign: 'center' }}>
            {isDiapo
              ? 'Une image est requise sur le diaporama'
              : "Une image est requise sur l'article"}
          </Typography>
        )}

        <Box
          sx={{
            mt: 3,
            display: 'flex',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: (t) => t.palette.grey[100],
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setSendMode('immediate')}
            sx={{
              flex: 1,
              py: 1.5,
              px: 2,
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              color:
                sendMode === 'immediate' ? 'primary.main' : 'text.secondary',
              fontWeight: sendMode === 'immediate' ? 700 : 400,
              backgroundColor:
                sendMode === 'immediate' ? 'background.paper' : 'transparent',
              borderRight: sendMode === 'immediate' ? '1px solid' : 'none',
              borderColor: 'divider',
              boxShadow: sendMode === 'immediate' ? 1 : 'none',
            }}
          >
            Envoi immédiat
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setSendMode('scheduled')}
            sx={{
              flex: 1,
              py: 1.5,
              px: 2,
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              color:
                sendMode === 'scheduled' ? 'primary.main' : 'text.secondary',
              fontWeight: sendMode === 'scheduled' ? 700 : 400,
              backgroundColor:
                sendMode === 'scheduled' ? 'background.paper' : 'transparent',
              borderLeft: sendMode === 'scheduled' ? '1px solid' : 'none',
              borderColor: 'divider',
              boxShadow: sendMode === 'scheduled' ? 1 : 'none',
            }}
          >
            Programmer
          </Box>
        </Box>

        {sendMode === 'scheduled' && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 2,
            }}
          >
            <DatePicker
              inputFormat="dd/MM/yyyy"
              label="Date"
              onChange={(date) => setScheduledDate(date)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                />
              )}
              value={scheduledDate}
            />
            <TimePicker
              ampm={false}
              inputFormat="HH:mm"
              label="Heure"
              onChange={(date) => setScheduledTime(date)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                />
              )}
              value={scheduledTime}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            onClose?.();
            closeDialog();
          }}
        >
          Annuler
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={() => onValidate()}
        >
          {sendMode === 'scheduled' ? 'PROGRAMMER' : 'ENVOYER'}
        </LoadingButton>
      </DialogActions>
    </>
  );
};
